import React, { useMemo, useState } from 'react';
import {
  Input,
  Message,
  Radio,
  RadioGroup,
  SelectPicker,
} from 'rsuite';
import { Button, FilterField, FilterToolbar, Modal, useLastPresent } from '@nsi-advisory-services/components';
import { showToast } from '../utils/toast';
import PageHeader from '../components/shared/PageHeader';
import {
  useConsolidateExpenseLookup,
  useExpenseLookupAdmin,
  useRenameExpenseLookup,
} from '../hooks/useQueries';

const KIND_LABELS = {
  vendors: { singular: 'Vendor', plural: 'Vendors' },
  customers: { singular: 'Customer', plural: 'Customers' },
};

const KIND_OPTIONS = Object.entries(KIND_LABELS).map(([value, { plural }]) => ({ label: plural, value }));

// 'YYYY-MM-DD' -> 'Aug 13, 2026', read as a local date.
const formatDay = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const USAGE_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'In use', value: 'used' },
  { label: 'Unused', value: 'unused' },
];

// Manages one lookup list (vendors or customers): search, rename, and
// consolidate. Keyed by `kind` in the parent so switching lists resets state.
function LookupManager({ kind, onKindChange }) {
  const labels = KIND_LABELS[kind];
  const listQuery = useExpenseLookupAdmin(kind, { enabled: true });
  const renameMutation = useRenameExpenseLookup(kind);
  const consolidateMutation = useConsolidateExpenseLookup(kind);

  const [search, setSearch] = useState('');
  const [usage, setUsage] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [renameRow, setRenameRow] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const renameRowShown = useLastPresent(renameRow); // keeps the dialog steady while it fades out
  const [consolidateOpen, setConsolidateOpen] = useState(false);
  const [consolidateTargetId, setConsolidateTargetId] = useState(null);

  const rows = useMemo(() => listQuery.data || [], [listQuery.data]);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      const used = Number(row.expense_count) > 0;
      if (usage !== 'all' && used !== (usage === 'used')) return false;
      return !query || String(row.name || '').toLowerCase().includes(query);
    });
  }, [rows, search, usage]);

  const selectedRows = useMemo(
    () => rows.filter((row) => selectedIds.includes(row.id)),
    [rows, selectedIds]
  );

  const toggleSelected = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
  };

  const openRename = (row) => {
    setRenameRow(row);
    setRenameValue(row.name || '');
  };

  const submitRename = async () => {
    const name = renameValue.trim();
    if (!name || !renameRow) return;
    try {
      await renameMutation.mutateAsync({ id: renameRow.id, name });
      showToast('success', `${labels.singular} updated.`);
      setRenameRow(null);
      setSelectedIds([]);
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to rename'));
    }
  };

  const openConsolidate = () => {
    setConsolidateTargetId(selectedIds[0] || null);
    setConsolidateOpen(true);
  };

  const submitConsolidate = async () => {
    if (!consolidateTargetId) return;
    try {
      await consolidateMutation.mutateAsync({ ids: selectedIds, targetId: consolidateTargetId });
      showToast('success', `Consolidated ${selectedIds.length} ${labels.plural.toLowerCase()}.`);
      setConsolidateOpen(false);
      setSelectedIds([]);
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to consolidate'));
    }
  };

  return (
    <>
      <FilterToolbar
        label={`${labels.singular} filters`}
        action={{
          label: selectedIds.length > 1 ? `Consolidate ${selectedIds.length}` : 'Consolidate',
          icon: 'bi-sign-merge-left',
          onClick: openConsolidate,
          disabled: selectedIds.length < 2,
        }}
      >
        <FilterField label="List" basis={170}>
          <SelectPicker data={KIND_OPTIONS} value={kind} onChange={(value) => value && onKindChange(value)} cleanable={false} searchable={false} block />
        </FilterField>
        <FilterField label="Search" icon="bi-search" basis={320}>
          <Input type="search" value={search} onChange={setSearch} placeholder={`${labels.singular} name`} aria-label={`Search ${labels.plural.toLowerCase()}`} />
        </FilterField>
        <FilterField label="Usage" basis={160}>
          <SelectPicker data={USAGE_OPTIONS} value={usage} onChange={(value) => setUsage(value || 'all')} cleanable={false} searchable={false} block />
        </FilterField>
      </FilterToolbar>

      <div className="nsi-table-wrap">
        <div className="bdc-table-head">
          <div className="bdc-table-head__heading">
            <h3 className="bdc-table-head__title">{labels.plural}</h3>
            <span className="bdc-count">{filtered.length}</span>
            {/* Always present, so selecting rows changes the words, not the layout. */}
            <span className="bdc-table-head__note">
              {selectedIds.length === 0 && 'Select two or more to consolidate them'}
              {selectedIds.length === 1 && '1 selected, pick one more to consolidate'}
              {selectedIds.length > 1 && `${selectedIds.length} selected`}
            </span>
          </div>
        </div>

        {listQuery.error ? (
          <Message type="error" showIcon>
            {String(listQuery.error?.message || listQuery.error)}
          </Message>
        ) : null}

        <table className="nsi-table bdc-table--narrow">
          <thead>
            <tr>
              <th style={{ width: 44 }} aria-label="Select" />
              <th>Name</th>
              <th style={{ width: 150 }}>First expensed</th>
              <th style={{ width: 130 }} className="nsi-table__right">Expenses</th>
            </tr>
          </thead>
          <tbody>
            {listQuery.isLoading ? (
              <tr>
                <td colSpan={4} className="nsi-table__empty">Loading…</td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={4} className="nsi-table__empty">
                  No {labels.plural.toLowerCase()} {search.trim() || usage !== 'all' ? 'match these filters.' : 'yet.'}
                </td>
              </tr>
            ) : filtered.map((row) => (
              <tr
                key={row.id}
                className="nsi-table__row"
                onClick={() => openRename(row)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openRename(row);
                  }
                }}
                tabIndex={0}
                role="button"
                title={`Rename this ${labels.singular.toLowerCase()}`}
              >
                {/* The checkbox picks rows for a consolidation, so it must not
                    also open the row. */}
                <td onClick={(event) => event.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedIds.includes(row.id)}
                    onChange={() => toggleSelected(row.id)}
                    aria-label={`Select ${row.name}`}
                  />
                </td>
                <td className="bdc-table-wrap fw-semibold">{row.name}</td>
                <td className="bdc-num">
                  {formatDay(row.first_expensed_on) || <span className="nsi-table__muted">—</span>}
                </td>
                <td className="nsi-table__right bdc-num">{row.expense_count}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Rename */}
        <Modal open={!!renameRow} onClose={() => setRenameRow(null)} size="xs">
          <Modal.Header>
            <Modal.Title>Rename {labels.singular}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted small">
              Renaming updates this name on all {renameRowShown?.expense_count ?? 0} expense
              {renameRowShown?.expense_count === 1 ? '' : 's'} that use it. If the new name already
              exists, the two are merged into one.
            </p>
            <Input
              value={renameValue}
              onChange={setRenameValue}
              onPressEnter={submitRename}
              placeholder={`${labels.singular} name`}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={() => setRenameRow(null)}>Cancel</Button>
            <Button
              variant="primary"
              loading={renameMutation.isPending}
              disabled={!renameValue.trim()}
              onClick={submitRename}
            >
              Save
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Consolidate */}
        <Modal open={consolidateOpen} onClose={() => setConsolidateOpen(false)} size="xs">
          <Modal.Header>
            <Modal.Title>Consolidate {labels.plural}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted small">
              Pick the name to keep. Every expense using the other selected
              {' '}{labels.plural.toLowerCase()} will be reassigned to it, and the rest are removed.
            </p>
            <RadioGroup value={consolidateTargetId} onChange={setConsolidateTargetId}>
              {selectedRows.map((row) => (
                <Radio key={row.id} value={row.id}>
                  {row.name} <span className="text-muted">({row.expense_count})</span>
                </Radio>
              ))}
            </RadioGroup>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={() => setConsolidateOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              loading={consolidateMutation.isPending}
              disabled={!consolidateTargetId}
              onClick={submitConsolidate}
            >
              Consolidate
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
}

export default function VendorCustomerAdminPage() {
  const [activeKind, setActiveKind] = useState('vendors');

  return (
    <div className="bdc-page">
      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          { label: 'Admin', to: '/admin' },
          { label: 'Configuration', to: '/admin/configuration' },
          { label: 'Vendors & Customers' },
        ]}
        icon="bi-shop"
        title="Manage Vendors & Customers"
      />

      <LookupManager key={activeKind} kind={activeKind} onKindChange={setActiveKind} />
    </div>
  );
}
